import {
  useIonViewWillEnter,
  useIonViewWillLeave,
  IonPage,
  IonContent,
} from "@ionic/react";
import React, {
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";

import { EOrderBy } from "../../../constants/boxDashboard";
import { IOnClickStudentAvatarParams, RealizationStamp } from "../../../state";
import {
  Context,
  fetchGearboxRealizations,
  fetchAllFeedbacks,
  fetchBoxesDashboard,
  fetchGearboxStudents,
  fetchGearboxRealizationDates,
  createRealizationStampReaction,
  createFeedback,
  updateFeedback,
  checkedTeacher,
  deleteFeedback,
  clearSuccessMessage,
  CREATE_FEEDBACK_SUCCESS_MESSAGE,
  UPDATE_FEEDBACK_SUCCESS_MESSAGE,
  DELETE_FEEDBACK_SUCCESS_MESSAGE,
  CREATE_REALIZATION_STAMP_REACTION_SUCCESS,
  addFavorite,
  removeFavorite,
} from "../../../store/teacher";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useIntersection from "../../../hooks/useIntersection";
import EmbeddedHeader from "../../../organisms/teacher/Header/EmbeddedHeader";
import Realizations from "../../../templates/teacher/Realizations";
import { SubmitterFilter } from "../../../templates/teacher/Realizations/SubmitterList";
import styles from "../common.module.scss";

import Badge from "./Badge";

const RealizationsBoxPage = () => {
  const { contextState, dispatch } = useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const { id } = useParams<{ id: string }>();
  const boxId = Number(id);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isPageActiveRef = useRef(false);

  const [filter, setFilter] = useState<SubmitterFilter>({ mode: "all" });
  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const dateParams = useMemo(
    () =>
      filter.mode === "date"
        ? { start_date: filter.date, end_date: filter.date }
        : undefined,
    [filter],
  );

  const box = contextState.boxesDashboard.find(b => b.id === boxId) ?? null;
  const boxTitle = (() => {
    if (!box?.title) return "";
    if (box.subject_id && box.year)
      return `${box.title}_${box.year}に提出されたストック`;
    return `${box.title}に提出されたストック`;
  })();

  const showStudentDetailFunc = ({
    class_id,
    student_id,
  }: IOnClickStudentAvatarParams) => {
    window.location.href = `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/students/${class_id}/${student_id}`;
  };

  const createFeedbackFunc = useCallback(
    (realization_id: number, content: string) => {
      dispatch(peep(createFeedback(realization_id, content)));
      const realization = contextState.realizations.find(
        r => r.id === realization_id,
      );
      if (realization && !realization.is_checked) {
        dispatch(peep(checkedTeacher(realization_id)));
      }
    },
    [dispatch, peep, contextState.realizations],
  );

  const createStampReactionFunc = useCallback(
    (realizationId: number, stamp: RealizationStamp) => {
      dispatch(
        peep(
          createRealizationStampReaction(realizationId, {
            type: "feedback",
            realization_stamp_id: stamp.id,
          }),
        ),
      );
      const realization = contextState.realizations.find(
        r => r.id === realizationId,
      );
      if (realization && !realization.is_checked) {
        dispatch(peep(checkedTeacher(realizationId)));
      }
    },
    [dispatch, peep, contextState.realizations],
  );

  const updateFeedbackFunc = useCallback(
    (feedbackId: number, content: string) => {
      dispatch(peep(updateFeedback(feedbackId, content)));
    },
    [dispatch, peep],
  );

  const deleteFeedbackFunc = useCallback(
    (feedbackId: number) => {
      dispatch(peep(deleteFeedback(feedbackId)));
    },
    [dispatch, peep],
  );

  const addFavoriteFunc = useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(addFavorite(id)));
    },
    [calling, dispatch, peep],
  );

  const removeFavoriteFunc = useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(removeFavorite(id)));
    },
    [calling, dispatch, peep],
  );

  const onChangeViewMonth = useCallback(
    (year: number, month: number) => {
      dispatch(peep(fetchGearboxRealizationDates(boxId, year, month)));
    },
    [boxId, dispatch, peep],
  );

  useIonViewWillEnter(() => {
    isPageActiveRef.current = true;
    dispatch(fetchAllFeedbacks());
    dispatch(
      fetchBoxesDashboard({
        order_by: EOrderBy.LATEST_REALIZATION_CREATED_AT,
      }),
    );
  });

  useIonViewWillLeave(() => {
    isPageActiveRef.current = false;
  });

  useEffect(() => {
    dispatch(peep(fetchGearboxRealizations(boxId, 1, dateParams)));
    dispatch(peep(fetchGearboxStudents(boxId, dateParams)));
  }, [boxId, dateParams, dispatch, peep]);

  useEffect(() => {
    if (!isPageActiveRef.current) return;
    if (
      contextState.success_message === CREATE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === CREATE_REALIZATION_STAMP_REACTION_SUCCESS
    ) {
      dispatch(fetchAllFeedbacks());
      dispatch(clearSuccessMessage());
    }
  }, [dispatch, contextState.success_message]);

  useIntersection({
    target: loadMoreRef,
    enabled:
      !calling && contextState.pagy_info.page < contextState.pagy_info.pages,
    onIntersect: () => {
      dispatch(
        peep(
          fetchGearboxRealizations(
            boxId,
            contextState.pagy_info.page + 1,
            dateParams,
          ),
        ),
      );
    },
  });

  return (
    <IonPage>
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        <div className={styles.container}>
          <EmbeddedHeader
            title={boxTitle}
            rightComponent={
              <Badge
                isArchived={box?.is_archived ?? false}
                nameStatus={box?.name_status ?? "publish"}
              />
            }
          />
          <Realizations
            pageType="box"
            box={box}
            calling={calling}
            loadMoreRef={loadMoreRef}
            submitter={{
              filter,
              onChangeFilter: setFilter,
              today,
              markedDates: contextState.gearboxRealizationDates,
              onChangeViewMonth,
            }}
            showStudentDetail={showStudentDetailFunc}
            createFeedback={createFeedbackFunc}
            createStampReaction={createStampReactionFunc}
            deleteFeedback={deleteFeedbackFunc}
            updateFeedback={updateFeedbackFunc}
            addFavorite={addFavoriteFunc}
            removeFavorite={removeFavoriteFunc}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default RealizationsBoxPage;
